// Iter90 — Shopify Migration Control. Single admin dashboard surfacing:
//   • Live processor flags + checkout readiness pct
//   • Migrate-all (dry-run + commit) with batch sizing
//   • Per-product readiness with the 7-checkpoint breakdown
//   • Completion report (totals, refund/POS/order sync status)
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, AlertTriangle, RefreshCw, Rocket, Wrench } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

function pct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="rounded-md border border-white/10 bg-black/30 p-3" data-testid={testid}>
      <div className="text-[0.6rem] uppercase tracking-[0.2em] text-lifted-dim">{label}</div>
      <div className={`text-2xl font-anton ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function AdminShopifyControl() {
  const [proc, setProc]      = useState(null);
  const [report, setReport]  = useState(null);
  const [comp, setComp]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]      = useState(false);
  const [batchSize, setBatchSize] = useState(50);
  const [err, setErr]        = useState("");
  const [ok, setOk]          = useState("");
  const [migrateResult, setMigrateResult] = useState(null);
  const [conn, setConn]            = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [variantMap, setVariantMap]     = useState(null);
  const [reportV2, setReportV2]         = useState(null);
  const [repairHandles, setRepairHandles] = useState("");
  const [repairBatchSize, setRepairBatchSize] = useState(15);
  const [repairProgress, setRepairProgress] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [p, r, c, cc, r2] = await Promise.all([
        api.get("/checkout/processor"),
        api.get("/admin/shopify/readiness-report?limit=200"),
        api.get("/admin/shopify/completion-report"),
        api.get("/admin/shopify/connection-check").catch((e) => ({ data: { __err: e?.response?.data || String(e) } })),
        api.get("/admin/shopify/readiness-report-v2").catch(() => ({ data: null })),
      ]);
      setProc(p.data); setReport(r.data); setComp(c.data);
      setConn(cc.data); setReportV2(r2.data);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function runMigrate(dryRun) {
    setBusy(true); setErr(""); setOk(""); setMigrateResult(null);
    try {
      const { data } = await api.post(
        `/admin/shopify/migrate-all?dry_run=${dryRun}&batch_size=${batchSize}`
      );
      setMigrateResult(data);
      if (dryRun) {
        setOk(`Dry-run scanned ${data.candidates} candidates. No writes performed.`);
      } else {
        setOk(`Migration committed: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped, ${(data.errors||[]).length} errors.`);
        refresh();
      }
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusy(false); }
  }

  // Iter91 — GraphQL-based repair-sync. Batches to avoid Cloudflare 100s timeout.
  async function runRepairSync(dryRun) {
    setRepairBusy(true); setErr(""); setOk(""); setRepairResult(null); setRepairProgress(null);
    const handleList = repairHandles.trim();
    const batchLimit = Math.max(1, Math.min(repairBatchSize, 50));
    let skip = 0;
    const totals = { scanned: 0, created_products: 0, repaired_products: 0,
                     created_variants: 0, published_products: 0,
                     checkout_ready: 0, still_unready: 0, errors: [] };
    try {
      while (true) {
        const params = new URLSearchParams({
          dry_run: String(dryRun),
          limit: String(batchLimit),
          skip: String(skip),
        });
        if (handleList) params.set("handles", handleList);
        const { data } = await api.post(`/admin/shopify/repair-sync?${params}`);
        totals.scanned           += data.scanned || 0;
        totals.created_products  += data.created_products || 0;
        totals.repaired_products += data.repaired_products || 0;
        totals.created_variants  += data.created_variants || 0;
        totals.published_products+= data.published_products || 0;
        totals.checkout_ready    += data.checkout_ready || 0;
        totals.still_unready     += data.still_unready || 0;
        totals.errors = totals.errors.concat(data.errors || []);
        setRepairProgress({ ...totals, total_matched: data.total_matched });
        // Stop if: using handles (single batch), nothing returned, or exhausted
        const exhausted = handleList || (data.scanned || 0) < batchLimit ||
                          (data.total_matched != null && skip + batchLimit >= data.total_matched);
        if (exhausted || (data.scanned || 0) === 0) break;
        skip += batchLimit;
        // brief pause to avoid hammering the server between batches
        await new Promise(r => setTimeout(r, 500));
      }
      setRepairResult(totals);
      if (dryRun) {
        setOk(`Repair dry-run: ${totals.scanned} products scanned.`);
      } else {
        setOk(`Repair complete: created ${totals.created_products}, repaired ${totals.repaired_products}, ${totals.created_variants} variants, published ${totals.published_products}, checkout-ready ${totals.checkout_ready}/${totals.scanned}.`);
        refresh();
      }
    } catch (e) {
      setErr(apiErrorMessage(e));
      if (totals.scanned > 0) setRepairResult(totals);
    } finally { setRepairBusy(false); }
  }

  async function runValidateVariants() {
    setRepairBusy(true); setErr(""); setOk(""); setVariantMap(null);
    try {
      const { data } = await api.post("/admin/shopify/validate-variant-map?limit=500");
      setVariantMap(data);
      setOk(`Variant validation: ${data.ok_count}/${data.scanned} can be added to cart. ${data.missing} missing, ${data.bad_gid_format} bad format, ${data.not_storefront_visible} not visible.`);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setRepairBusy(false); }
  }

  if (loading && !proc) {
    return (
      <div className="p-10 flex items-center gap-2 text-lifted-dim" data-testid="shopify-control-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Shopify control panel…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="admin-shopify-control">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow text-lifted-green">SHOPIFY · ITER90</div>
          <h1 className="font-anton uppercase text-4xl text-white">Migration Control</h1>
          <p className="text-lifted-dim text-sm mt-1">
            Push every Emergent product to Shopify · monitor checkout readiness ·
            audit refund + POS + order sync.
          </p>
        </div>
        <button
          onClick={refresh}
          className="btn-lifted-outline text-xs inline-flex items-center gap-1"
          data-testid="shopify-control-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </header>

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/10 rounded-md p-3 text-sm text-rose-200"
              data-testid="shopify-control-error">
          {err}
        </div>
      )}
      {ok && (
        <div className="border border-lifted-green/40 bg-lifted-green/10 rounded-md p-3 text-sm text-lifted-green"
              data-testid="shopify-control-ok">
          {ok}
        </div>
      )}

      {/* Flags row */}
      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat
          label="Checkout readiness"
          value={pct(report?.readiness_pct)}
          accent={(report?.readiness_pct ?? 0) >= 95 ? "text-lifted-green" : "text-amber-300"}
          testid="stat-checkout-readiness"
        />
        <Stat
          label="Total products"
          value={report?.total_products ?? "—"}
          testid="stat-total"
        />
        <Stat
          label="Ready"
          value={report?.ready_count ?? "—"}
          accent="text-lifted-green"
          testid="stat-ready"
        />
        <Stat
          label="Unready"
          value={report?.unready_count ?? "—"}
          accent="text-amber-300"
          testid="stat-unready"
        />
        <Stat
          label="Shopify Active"
          value={proc?.shopify_active ? "ON" : "OFF"}
          accent={proc?.shopify_active ? "text-lifted-green" : "text-rose-300"}
          testid="stat-shopify-active"
        />
        <Stat
          label="Shopify Checkout"
          value={proc?.shopify_checkout_active ? "ON" : "OFF"}
          accent={proc?.shopify_checkout_active ? "text-lifted-green" : "text-rose-300"}
          testid="stat-shopify-checkout"
        />
      </section>

      {/* Iter91 — Connection check */}
      {conn && (
        <section className="glass-panel p-5 space-y-2" data-testid="connection-check">
          <h2 className="font-anton uppercase text-xl text-white flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-lifted-green" /> Production Shopify connection check
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div data-testid="conn-shop-domain">Shop: <span className="text-white font-mono">{conn.shop_domain || "—"}</span></div>
            <div data-testid="conn-admin-token">
              Admin token: <span className={conn.admin_token_present ? "text-lifted-green" : "text-rose-300"}>
                {conn.admin_token_present ? `present (${conn.admin_auth_method})` : "MISSING"}
              </span>
            </div>
            <div data-testid="conn-storefront-token">
              Storefront token: <span className={conn.storefront_token_present ? "text-lifted-green" : "text-rose-300"}>
                {conn.storefront_token_present ? "present" : "MISSING"}
              </span>
            </div>
            <div data-testid="conn-write-enabled">
              Write enabled: <span className={conn.write_enabled ? "text-lifted-green" : "text-rose-300"}>
                {conn.write_enabled ? "YES" : "NO"}
              </span>
            </div>
            <div>Admin REST: <span className={conn.admin_api_shop_success ? "text-lifted-green" : "text-rose-300"}>
              {conn.admin_api_shop_success ? "OK" : "FAIL"}
            </span></div>
            <div>Admin GraphQL: <span className={conn.admin_api_graphql_success ? "text-lifted-green" : "text-rose-300"}>
              {conn.admin_api_graphql_success ? "OK" : "FAIL"}
            </span></div>
            <div>Storefront GraphQL: <span className={conn.storefront_api_success ? "text-lifted-green" : "text-rose-300"}>
              {conn.storefront_api_success ? "OK" : "FAIL"}
            </span></div>
            <div data-testid="conn-ready">
              Migration ready: <span className={conn.ready_for_migration ? "text-lifted-green" : "text-amber-300"}>
                {conn.ready_for_migration ? "YES" : "NO"}
              </span>
            </div>
          </div>
          {conn.publications?.length > 0 && (
            <div className="text-xs text-lifted-dim">
              Publications: {conn.publications.map((p) => p.name).join(" · ")}
            </div>
          )}
          {conn.scopes_detected?.length > 0 && (
            <details className="text-xs text-lifted-dim">
              <summary className="cursor-pointer">Scopes ({conn.scopes_detected.length})</summary>
              <div className="font-mono mt-1">{conn.scopes_detected.join(", ")}</div>
            </details>
          )}
          {(conn.errors || []).length > 0 && (
            <details className="text-xs text-rose-300" data-testid="conn-errors">
              <summary className="cursor-pointer">{conn.errors.length} connection error(s)</summary>
              <ul className="mt-1 space-y-1">
                {conn.errors.map((e, i) => (
                  <li key={i} className="font-mono">[{e.step}] {JSON.stringify(e.detail).slice(0, 240)}</li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {/* Iter91 — Repair Shopify Sync */}
      <section className="glass-panel p-5 border-amber-400/40 space-y-3" data-testid="repair-sync-panel">
        <h2 className="font-anton uppercase text-xl text-white flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-300" /> Repair Shopify Sync (GraphQL)
        </h2>
        <p className="text-lifted-dim text-xs">
          The real production fix. Walks every LATM product → creates missing
          Shopify products → repairs stale IDs → bulk-creates all variants →
          publishes to Online Store → verifies the variant is reachable
          via the Storefront API. Idempotent. Safe to re-run.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            value={repairHandles}
            onChange={e => setRepairHandles(e.target.value)}
            placeholder="handles (comma-sep, blank = all)"
            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white w-72 placeholder:text-lifted-dim"
            data-testid="repair-handles-input"
          />
          <input
            type="number"
            min={1} max={50}
            value={repairBatchSize}
            onChange={e => setRepairBatchSize(Number(e.target.value))}
            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white w-20"
            title="Batch size per request (1-50)"
            data-testid="repair-batch-size-input"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => runRepairSync(true)}
            disabled={repairBusy}
            className="btn-lifted-outline text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="repair-dry-run-btn"
          >
            {repairBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Dry-run
          </button>
          <button
            onClick={() => {
              if (!conn?.ready_for_migration) {
                if (!window.confirm("Connection check failed — proceed anyway?")) return;
              } else if (!window.confirm("Commit Shopify repair-sync to live store? This writes real products + publishes to Online Store.")) {
                return;
              }
              runRepairSync(false);
            }}
            disabled={repairBusy}
            className="btn-lifted text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="repair-shopify-sync-btn"
          >
            {repairBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
            Repair Shopify Sync
          </button>
          <button
            onClick={runValidateVariants}
            disabled={repairBusy}
            className="btn-lifted-outline text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="validate-variants-btn"
          >
            Validate Variant Map
          </button>
        </div>
        {repairProgress && repairBusy && (
          <div className="text-xs text-lifted-dim mt-2" data-testid="repair-progress">
            <div className="flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-amber-300" />
              <span>Processing… {repairProgress.scanned}
                {repairProgress.total_matched ? ` / ${repairProgress.total_matched}` : ""} products
                — created {repairProgress.created_products},
                repaired {repairProgress.repaired_products},
                published {repairProgress.published_products}
                {repairProgress.errors?.length > 0 ? `, ${repairProgress.errors.length} error(s)` : ""}
              </span>
            </div>
            {repairProgress.total_matched > 0 && (
              <div className="mt-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-lifted-green transition-all"
                  style={{ width: `${Math.min(100, (repairProgress.scanned / repairProgress.total_matched) * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}
        {repairResult && (
          <div className="text-xs text-lifted-dim grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-2"
                data-testid="repair-result">
            <div>Scanned: <span className="text-white">{repairResult.scanned}</span></div>
            <div>Created: <span className="text-lifted-green">{repairResult.created_products}</span></div>
            <div>Repaired: <span className="text-lifted-green">{repairResult.repaired_products}</span></div>
            <div>Variants: <span className="text-lifted-green">{repairResult.created_variants}</span></div>
            <div>Published: <span className="text-lifted-green">{repairResult.published_products}</span></div>
            <div>Checkout-ready: <span className="text-lifted-green">{repairResult.checkout_ready}</span></div>
            <div>Still unready: <span className="text-amber-300">{repairResult.still_unready}</span></div>
          </div>
        )}
        {variantMap && (
          <div className="text-xs text-lifted-dim grid grid-cols-2 md:grid-cols-5 gap-2 mt-2"
                data-testid="variant-map-result">
            <div>Scanned: <span className="text-white">{variantMap.scanned}</span></div>
            <div>OK: <span className="text-lifted-green">{variantMap.ok_count}</span></div>
            <div>Missing GID: <span className="text-amber-300">{variantMap.missing}</span></div>
            <div>Bad format: <span className="text-rose-300">{variantMap.bad_gid_format}</span></div>
            <div>Not visible: <span className="text-rose-300">{variantMap.not_storefront_visible}</span></div>
          </div>
        )}
        {repairResult?.errors?.length > 0 && (
          <details className="text-xs text-rose-300 mt-2" data-testid="repair-errors">
            <summary className="cursor-pointer">{repairResult.errors.length} error(s)</summary>
            <ul className="mt-1 space-y-1 max-h-48 overflow-y-auto font-mono">
              {repairResult.errors.slice(0, 30).map((e, i) => (
                <li key={i}>[{e.handle || "?"}] {(e.action || "")}: {JSON.stringify(e.userErrors || e.error || e).slice(0, 220)}</li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Iter91 — Strict readiness v2 */}
      {reportV2 && (
        <section className="glass-panel p-5" data-testid="readiness-v2">
          <h2 className="font-anton uppercase text-xl text-white mb-3 flex items-center gap-2">
            Strict checkout-readiness (live Shopify check)
            <span className={`text-xs ${reportV2.readiness_pct === 100 ? "text-lifted-green" : "text-amber-300"}`}>
              {reportV2.readiness_pct}% ready
            </span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            {Object.entries(reportV2.checkpoints || {}).map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-md p-2 bg-black/30"
                    data-testid={`v2-checkpoint-${k}`}>
                <div className="text-[0.55rem] uppercase tracking-[0.18em] text-lifted-dim">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="text-white font-anton text-lg">{v}</div>
                <div className="text-[0.55rem] text-lifted-dim">
                  / {reportV2.total_products}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Migrate-all action */}
      <section className="glass-panel p-5 space-y-3" data-testid="migrate-all-panel">
        <h2 className="font-anton uppercase text-xl text-white flex items-center gap-2">
          <Rocket className="h-4 w-4 text-lifted-green" /> Migrate all unmatched products
        </h2>
        <p className="text-lifted-dim text-xs">
          Pushes every Emergent product without a Shopify ID to Shopify (variants,
          images, SKU, barcode, weight, options, tags, inventory).
          Idempotent. Dry-run shows what would be sent.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-lifted-dim inline-flex items-center gap-2">
            Batch size
            <input
              type="number"
              min={1}
              max={500}
              value={batchSize}
              onChange={(e) => setBatchSize(parseInt(e.target.value || "50"))}
              data-testid="migrate-batch-size"
              className="input-lifted text-sm w-24"
            />
          </label>
          <button
            onClick={() => runMigrate(true)}
            disabled={busy}
            className="btn-lifted-outline text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="migrate-dry-run-btn"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Dry-run
          </button>
          <button
            onClick={() => {
              if (!window.confirm("Commit migration to live Shopify? This writes real products.")) return;
              runMigrate(false);
            }}
            disabled={busy || !proc?.shopify_configured}
            className="btn-lifted text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="migrate-commit-btn"
            title={proc?.shopify_configured ? "" : "Shopify credentials not configured in this env"}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
            Commit
          </button>
        </div>
        {migrateResult && (
          <div className="text-xs text-lifted-dim grid grid-cols-2 md:grid-cols-4 gap-2 mt-2"
                data-testid="migrate-result">
            <div>Candidates: <span className="text-white">{migrateResult.candidates}</span></div>
            <div>Created: <span className="text-lifted-green">{migrateResult.created}</span></div>
            <div>Updated: <span className="text-lifted-green">{migrateResult.updated}</span></div>
            <div>Errors: <span className="text-rose-300">{(migrateResult.errors || []).length}</span></div>
          </div>
        )}
      </section>

      {/* 7-checkpoint matrix */}
      {report?.checkpoints && (
        <section className="glass-panel p-5" data-testid="readiness-checkpoints">
          <h2 className="font-anton uppercase text-xl text-white mb-3">7-point checkout readiness</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            {Object.entries(report.checkpoints).map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-md p-2 bg-black/30"
                    data-testid={`checkpoint-${k}`}>
                <div className="text-[0.55rem] uppercase tracking-[0.18em] text-lifted-dim">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="text-white font-anton text-lg">{v}</div>
                <div className="text-[0.55rem] text-lifted-dim">
                  / {report.total_products}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Unready sample */}
      {report?.unready_sample?.length > 0 && (
        <section className="glass-panel p-5" data-testid="unready-list">
          <h2 className="font-anton uppercase text-xl text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-300" /> Products requiring attention
            <span className="text-xs text-lifted-dim">({report.unready_sample.length} shown)</span>
          </h2>
          <ul className="space-y-1 max-h-96 overflow-y-auto text-xs">
            {report.unready_sample.slice(0, 50).map((p) => (
              <li key={p.handle}
                  className="flex items-center justify-between gap-3 border-b border-white/5 py-1"
                  data-testid={`unready-row-${p.handle}`}>
                <div className="text-white truncate flex-1">{p.title || p.handle}</div>
                <div className="text-lifted-dim text-[0.65rem] truncate">
                  {(p.missing || []).join(" · ")}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Completion summary */}
      {comp && (
        <section className="glass-panel p-5" data-testid="completion-report">
          <h2 className="font-anton uppercase text-xl text-white mb-3">Completion report</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Stat label="Total products" value={comp.totals?.total_products ?? "—"} testid="comp-total" />
            <Stat label="With Shopify ID" value={comp.totals?.products_with_shopify_id ?? "—"} testid="comp-with-pid" />
            <Stat label="With Variant ID" value={comp.totals?.products_with_variant_id ?? "—"} testid="comp-with-vid" />
            <Stat label="Manual attention" value={comp.totals?.products_needing_manual_attention ?? 0} testid="comp-manual" />
            <Stat label="Variants synced" value={`${comp.totals?.variants_synced ?? 0}/${comp.totals?.variants_total ?? 0}`} testid="comp-variants" />
            <Stat label="Shopify orders" value={comp.order_sync_status?.shopify_orders_in_emergent ?? 0} testid="comp-orders" />
            <Stat label="Paid Shopify orders" value={comp.order_sync_status?.shopify_paid_orders ?? 0} accent="text-lifted-green" testid="comp-paid" />
            <Stat
              label="Refund ready"
              value={comp.refund_readiness?.ready ? "YES" : "NO"}
              accent={comp.refund_readiness?.ready ? "text-lifted-green" : "text-amber-300"}
              testid="comp-refund-ready"
            />
          </div>
          <div className="mt-3 text-xs text-lifted-dim">
            <strong>POS sync</strong>: webhook endpoint <code className="text-lifted-green">{comp.pos_sync_status?.webhooks_endpoint}</code>;
            handled events: {(comp.pos_sync_status?.events_handled || []).join(", ")};
            secret configured: {comp.pos_sync_status?.secret_configured ? "yes" : "no"}.
          </div>
          {comp.refund_readiness?.error && (
            <div className="mt-2 text-xs text-amber-300">
              Refund check error: {comp.refund_readiness.error}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
